import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import React from "react";

type ThreadStoreModel = Record<Moim.Id, Moim.Forum.IThread>;

const THREAD_QUERY_KEY = ["threads"] as const;

export const useThreadQuery = () => {
  const { data, ...rest } = useSuspenseQuery<ThreadStoreModel>({
    queryKey: THREAD_QUERY_KEY,
    queryFn: async () => {
      // NOTE: 지금은 thread list를 가져올 필요가 없어서 빈 객체를 반환.
      return {};
    },
    initialData: {},
    initialDataUpdatedAt: 0,
    staleTime: 1000 * 60 * 60, // 1 hour
    select: data => {
      return data;
    },
  });

  return {
    threads: data,
    ...rest,
  };
};

export const useMutationThreads = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: Moim.Forum.IThread) => data,
    onMutate: async (data: Moim.Forum.IThread) => {
      const touchKey = THREAD_QUERY_KEY;

      await queryClient.cancelQueries({
        queryKey: touchKey,
      });

      const previousThreads =
        queryClient.getQueryData<ThreadStoreModel>(touchKey);

      let touchedThread: Moim.Forum.IThread = data;
      const prevThread = previousThreads[data.id];
      if (prevThread) {
        touchedThread = {
          ...prevThread,
          ...data,
        };
      }

      queryClient.setQueryData<ThreadStoreModel>(touchKey, {
        ...previousThreads,
        [data.id]: touchedThread,
      });

      return { previousThreads };
    },
    onError(err, newData, context) {
      if (context?.previousThreads) {
        queryClient.setQueryData(THREAD_QUERY_KEY, context.previousThreads);
      }
    },
  });

  const storeThread = React.useCallback(
    (newThread: Moim.Forum.IThread) => {
      try {
        mutation.mutate(newThread);
      } catch {
        console.error("Failed to store thread");
      }
    },
    [mutation],
  );

  return {
    ...mutation,
    storeThread,
  };
};
